<script setup lang="ts">
/**
 * En-tête de la page publique d'une association (et de ses pages « tout
 * lister ») — charte « apple » (Karim 2026-10-18) : logo qui chevauche le
 * panneau arrondi, catégorie en bleu au-dessus d'un grand nom serré, présentation en corps
 * de lecture, bouton pilule « Soutenir ». Slot par défaut : sous le bouton
 * (chiffres de l'association sur sa page).
 */
import { TITRE_APPLE } from "~/utils/titresApple";

interface HeaderOrg {
  name: string;
  logo: string | null;
  cover_image: string | null;
  // Bannière de secours (cagnotte la plus dotée) servie par l'API quand
  // l'asso n'a pas de cover_image. Cf. publicOrganizationController.
  fallback_banner?: string | null;
  category: string | null;
  description: string | null;
  payment_slug: string | null;
  is_verified?: boolean;
}

const { t } = useI18n();

withDefaults(
  defineProps<{
    org: HeaderOrg;
    assetsUrl: string;
    categoryLabel: Record<string, string>;
    getLogoUrl: (fileId: string | null) => string | null;
    /**
     * Balise du nom de l'association. `h1` sur la page de l'association, dont
     * c'est le sujet ; `p` sur ses pages « tout lister », dont le sujet est la
     * liste — le `h1` y revient à « Tous les événements ». Deux `h1` sur une
     * même page brouillent le sujet aux yeux d'un moteur comme d'un lecteur
     * d'écran. Le rendu visuel est identique dans les deux cas.
     */
    baliseNom?: "h1" | "p";
    /**
     * Bouton « Soutenir » dans l'en-tête. Masqué sur les pages « tout
     * lister » : elles portent le bouton FLOTTANT, qui reste accessible quand
     * on descend dans la liste — deux boutons identiques à quelques
     * centimètres l'un de l'autre n'aident personne. Karim 2026-09-12.
     */
    afficherCta?: boolean;
  }>(),
  { baliseNom: "h1", afficherCta: true },
);
</script>

<template>
  <div class="entete relative">
    <!-- ⚠️ LA COUVERTURE N'EST PLUS ICI : elle est FIXE, rendue par
         `AssociationsPageBanniereFixe` juste avant le panneau arrondi qui
         contient ce bloc. Karim 2026-09-12. -->
    <div class="relative z-20 mx-auto flex max-w-[1120px] flex-col items-center px-4 text-center sm:px-8">
      <!-- Le logo chevauche le bord arrondi du panneau. Le panneau remonte de
           68 px sur la bannière et porte 32 px de `pt` ; on remonte donc le
           logo de 32 px + la moitié de sa hauteur (104 px → 52, 120 px → 60). -->
      <div class="entree relative -mt-[84px] w-fit sm:-mt-[92px]">
        <div class="flex size-[104px] items-center justify-center overflow-hidden rounded-[28px] border-4 border-white bg-white shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)] sm:size-[120px] sm:rounded-[32px]">
          <img
            v-if="org.logo"
            :src="getLogoUrl(org.logo)!"
            :alt="org.name"
            class="h-full w-full object-cover"
          />
          <UIcon v-else name="i-lucide-building-2" class="size-10 text-[#86868b]" />
        </div>
        <!-- Badge certifié — association vérifiée par l'équipe Donoor -->
        <span
          v-if="org.is_verified"
          class="absolute -top-2 -right-2 flex size-8 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/5"
          :title="t('association.header.badgeVerifie')"
        >
          <UIcon name="i-lucide-badge-check" class="size-6 text-(--color-main)" />
        </span>
      </div>

      <div class="mt-6 max-w-[820px]">
        <p v-if="org.category" class="entree mb-3 text-[15px] font-semibold text-(--color-main) sm:text-[17px]" style="--d: 60ms">
          {{ categoryLabel[org.category] || t('association.header.categorieParDefaut') }}
        </p>
        <component
          :is="baliseNom"
          class="entree text-[36px] text-[#1d1d1f] [text-wrap:balance] sm:text-[48px] lg:text-[56px]"
          style="--d: 120ms"
          :style="TITRE_APPLE"
        >
          {{ org.name }}
        </component>
        <p v-if="org.description" class="entree mx-auto mt-5 line-clamp-4 max-w-[680px] text-[17px] font-medium leading-[1.5] text-[#424245] sm:text-[19px]" style="--d: 180ms">
          {{ org.description }}
        </p>
      </div>

      <!-- CTA Soutenir -->
      <BoutonApple
        v-if="afficherCta && org.payment_slug"
        :to="`/pay/${org.payment_slug}`"
        target="_blank"
        grand
        class="entree mt-8 max-w-full"
        style="--d: 240ms"
      >
        <UIcon name="i-lucide-heart" class="size-5 shrink-0" />
        <span class="min-w-0 truncate">{{ t('association.header.soutenir', { nom: org.name }) }}</span>
      </BoutonApple>

      <slot />
    </div>
  </div>
</template>

<style scoped>
.entree {
  animation: entree 900ms cubic-bezier(0.28, 0, 0.22, 1) both;
  animation-delay: var(--d, 0ms);
}
@keyframes entree {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .entree {
    animation: none;
  }
}
</style>
